// Shared CMA feature/tier math (CommonJS, no imports): the app's CMA Builder (via src/services/cmaFeatures.js)
// and the MCP run_cma tool use this one copy.
// Feature definitions, tier detection and adjustment math for the Resi CMA Builder.
// Each feature has tiers. The adjustment for a comp is the subject's tier value
// minus the comp's tier value. Base tiers are worth $0 and have no input.

const toNumber = (s) => {
  const n = parseFloat(String(s ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const YES_NO = [{ id: 'no', label: 'No', base: true }, { id: 'yes', label: 'Yes' }];
const YES_NO_VALUE = (value) => [{ id: 'no', label: 'No', base: true }, { id: 'yes', label: 'Yes', value }];

// Standard features the agent always sees. Defaults are the agent's starting numbers.
const STANDARD_FEATURES = [
  { key: 'pool', label: 'Pool and spa', tiers: [
    { id: 'none', label: 'No pool or spa', base: true },
    { id: 'spa_only', label: 'Above-ground spa only', value: 5000 },
    { id: 'basic', label: 'Basic pool', value: 80000 },
    { id: 'basic_spa', label: 'Basic pool with spa', value: 100000 },
    { id: 'custom_spa', label: 'Custom pool with spa', value: 125000 },
  ] },
  { key: 'outdoorKitchen', label: 'Outdoor kitchen', tiers: [
    { id: 'none', label: 'None', base: true },
    { id: 'grill', label: 'Built-in grill' },
    { id: 'full', label: 'Full outdoor kitchen' },
  ] },
  { key: 'lanai', label: 'Lanai or screen enclosure', tiers: [
    { id: 'standard', label: 'Standard lanai or screen enclosure', base: true },
    { id: 'extended', label: 'Extended lanai or extra-large screen enclosure', value: 10000 },
  ] },
  { key: 'generator', label: 'Whole-home generator', tiers: YES_NO_VALUE(10000) },
  { key: 'hurricane', label: 'Hurricane protection', tiers: [
    { id: 'none', label: 'None', base: true },
    { id: 'shutters', label: 'Attached or electric shutters', value: 10000 },
    { id: 'storm_screens', label: 'Storm Smart screens', value: 20000 },
  ] },
  { key: 'view', label: 'View and lot', tiers: [
    { id: 'landscape', label: 'Landscape', base: true },
    { id: 'corner', label: 'Corner lot', value: -10000 },
    { id: 'preserve', label: 'Preserve (typical $50k-$75k)', value: 62500 },
    { id: 'lake', label: 'Lake (typical ~$50k)', value: 50000 },
    { id: 'lake_end', label: 'End of lake', value: 35000 },
  ] },
  { key: 'carpet', label: 'Carpet in bedrooms', tiers: YES_NO_VALUE(-10000) },
];

// Features found in comp descriptions. The agent can add more by hand.
const EXTRA_PATTERNS = [
  { key: 'x_closets', label: 'Custom closets', re: /custom closets?|closet system/i, value: 5000 },
  { key: 'x_wall', label: 'Tile feature wall', re: /feature wall|accent wall/i, value: 5000 },
  { key: 'x_tray', label: 'Tray ceiling', re: /tray ceiling/i },
  { key: 'x_crown', label: 'Crown molding', re: /crown molding/i },
  { key: 'x_solar', label: 'Solar panels', re: /solar (panels?|power|system)/i },
  { key: 'x_office', label: 'Office or bonus room', re: /bonus room|loft|office/i },
  { key: 'x_floors', label: 'Upgraded flooring', re: /luxury vinyl|hardwood|wood floors|tile throughout|upgraded (flooring|tile)/i },
  { key: 'x_appliances', label: 'Upgraded appliances', re: /upgraded (stainless )?appliances|stainless steel appliances|upgraded kitchen/i },
  { key: 'x_water', label: 'Waterfall or water feature', re: /waterfall|water feature/i },
  { key: 'x_pavers', label: 'Pavers or upgraded patio', re: /pavers?/i },
];

// Reads a comp or subject record block (from the MLS report) into the fields the rules use.
// PDF text often splits words with stray spaces ("Gener ator"), so matching uses text with spaces removed.
const sq = (s) => String(s || '').replace(/\s+/g, '');

// Groups MLS status wording into Sold, Pending or Active so every variant lands in the right table.
// Active Under Contract / Active With Contract / contingent count as Pending; Coming Soon counts as Active.
function normalizeStatus(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  if (/sold|closed/i.test(t)) return 'Sold';
  if (/pending|under\s*contract|with\s*contract|contingen|^AWC$|^UC$|^P$/i.test(t)) return 'Pending';
  if (/active|coming\s*soon|^ACT$|^A$|^CS$|back\s*on\s*market/i.test(t)) return 'Active';
  return t.split(/\s+/)[0];
}

function readRecord(block) {
  const text = block || '';
  const grab = (re) => ((text.match(re) || [])[1] || '').trim();
  const description = grab(/Property Information:\s*([\s\S]*?)\n\s*Ownership:/i);
  const privatePool = grab(/Private Pool:([\s\S]*?)Private Spa:/i);
  const statusText = grab(/Status:\s*([A-Za-z][A-Za-z \t/-]*?)\s*(?:\(|\n|$)/);
  const status = normalizeStatus(statusText);
  // pdf.js often splits labels into separate pieces ("List   Price:"), so allow any spacing.
  const soldPrice = toNumber(grab(/Sold\s*Price:\s*\$([\d,]+)/));
  const listPrice = toNumber(grab(/List\s*Price:\s*\$([\d,]+)/));
  return {
    status,
    statusText,
    statusDate: grab(/Status:\s*[A-Za-z][A-Za-z \t/-]*?\s*\(([\d/]+)\)/),
    mls: grab(/MLS#:\s*(\d+)/),
    address: grab(/Address:\s*([^\n]+)/),
    listPrice,
    soldPrice,
    price: (/sold/i.test(status) && soldPrice) || listPrice,
    description,
    text: {
      pool: sq(privatePool),
      exterior: sq(grab(/Exterior Features:([\s\S]*?)Interior Features:/i)),
      equipment: sq(grab(/Equipment:([\s\S]*?)Exterior Features:/i)),
      storm: sq(grab(/Storm Protection:([^\n]*)/i)),
      view: sq(grab(/\bView:([^\n]*)/)),
      flooring: sq(grab(/Flooring:([\s\S]*?)Cooling:/i)),
    },
    hasPool: /Private Pool:\s*Yes/i.test(text),
    hasSpa: /Private Spa:\s*Yes/i.test(text),
    corner: /corner\s*lot/i.test(sq(description)),
  };
}

// Tier a record falls into for one standard feature.
function tierFor(key, rec) {
  const t = rec.text;
  switch (key) {
    case 'pool': {
      const spa = rec.hasSpa;
      if (rec.hasPool) {
        const custom = /salt|custom|upgrad|waterfall/i.test(t.pool);
        if (spa) return custom ? 'custom_spa' : 'basic_spa';
        return 'basic';
      }
      return spa ? 'spa_only' : 'none';
    }
    case 'outdoorKitchen':
      if (/outdoorkitchen|summerkitchen/i.test(t.exterior)) return 'full';
      if (/grill/i.test(t.exterior)) return 'grill';
      return 'none';
    case 'lanai':
      // Only an extended or extra-large lanai/screen enclosure called out in the description adjusts.
      if (/extendedlanai|extendedscreen|extendedenclosure|extralargescreen|extralargelanai|oversizedlanai|oversizedscreen|largescreenenclosure|extended.{0,15}(lanai|screen|enclosure)|(extra|oversized).{0,10}(lanai|screen|enclosure)/i.test(sq(rec.description))) return 'extended';
      return 'standard';
    case 'generator': return /generator/i.test(t.equipment) ? 'yes' : 'no';
    case 'hurricane': {
      const s = t.storm;
      if (/stormsmart|screen/i.test(s)) return 'storm_screens';
      if (/shutter/i.test(s) && !/manual|aluminum|garage/i.test(s)) return 'shutters';
      if (/(attached|electric|motor|automatic).{0,20}shutter|shutter.{0,20}(attached|electric|motor|automatic)/i.test(s)) return 'shutters';
      return 'none';
    }
    case 'view': {
      // Corner lot takes priority, then end of lake, lake, preserve, landscape.
      if (rec.corner) return 'corner';
      if (/endoflake|lakeend|end-of-lake/i.test(sq(rec.description))) return 'lake_end';
      if (/lake/i.test(t.view)) return 'lake';
      if (/preserve/i.test(t.view)) return 'preserve';
      return 'landscape';
    }
    case 'carpet': return /carpet/i.test(t.flooring) ? 'yes' : 'no';
    default: return null;
  }
}

// Extra features found in the description, as yes/no. Patterns are matched against text with spaces removed.
function extrasFor(rec) {
  const d = sq(rec.description);
  return EXTRA_PATTERNS
    .filter((p) => new RegExp(p.re.source.replace(/\s+/g, ''), 'i').test(d))
    .map((p) => p.key);
}

// Every feature row the agent sees: standard features plus any found in the comps and added by hand.
function featureRows(comps, customFeatures = []) {
  const seen = new Set();
  const extras = [];
  comps.forEach((c) => extrasFor(c).forEach((k) => {
    if (!seen.has(k)) {
      seen.add(k);
      const pat = EXTRA_PATTERNS.find((p) => p.key === k);
      extras.push({ key: k, label: pat.label, tiers: YES_NO_VALUE(pat.value), found: true });
    }
  }));
  const custom = customFeatures.map((f) => ({ key: f.key, label: f.label, tiers: YES_NO, custom: true }));
  return [...STANDARD_FEATURES, ...extras, ...custom];
}

// Dollar value of a tier. Base tiers are always $0.
function tierValue(row, tierId, values) {
  const tier = row.tiers.find((x) => x.id === tierId);
  if (!tier || tier.base) return 0;
  const entered = values?.[row.key]?.[tierId];
  const v = toNumber(entered);
  if (v !== null) return v;
  return tier.value ?? 0;
}

// Features where the subject and comp tiers differ, with the dollar amount for each.
function adjustmentLines(rows, subjectTiers, comp, values) {
  const lines = [];
  rows.forEach((row) => {
    const subjectTier = subjectTiers[row.key] ?? row.tiers[0].id;
    // Pool comps are only used when the subject has a pool, so the pool never adjusts a no-pool subject.
    if (row.key === 'pool' && (subjectTier === 'none' || subjectTier === 'spa_only')) return;
    const compTier = row.custom
      ? (comp.customYes?.[row.key] ? 'yes' : 'no')
      : row.found
        ? (extrasFor(comp).includes(row.key) ? 'yes' : 'no')
        : tierFor(row.key, comp);
    if (subjectTier === compTier) return;
    const amount = tierValue(row, subjectTier, values) - tierValue(row, compTier, values);
    if (amount) lines.push({ key: row.key, label: row.label, subjectTier, compTier, amount });
  });
  return lines;
}

// Subject features that are set but have no dollar value entered yet.
function missingValues(rows, subjectTiers, values) {
  return rows.filter((row) => {
    const tier = subjectTiers[row.key] ?? row.tiers[0].id;
    const t = row.tiers.find((x) => x.id === tier);
    if (!t || t.base) return false;
    return toNumber(values?.[row.key]?.[tier]) === null && t.value === undefined;
  }).map((row) => row.label);
}

// Adjusted comp prices, split by status, plus the average adjusted sold price.
function runAnalysis({ comps, rows, subjectTiers, values }) {
  const withPrice = (comps || []).filter((c) => c.price);
  const adjusted = withPrice.map((c) => {
    const lines = adjustmentLines(rows, subjectTiers, c, values);
    const total = lines.reduce((sum, l) => sum + l.amount, 0);
    return { ...c, lines, adjustment: total, adjusted: (c.price || 0) + total };
  });
  const by = (re) => adjusted.filter((c) => re.test(c.status || ''));
  const sold = by(/sold/i);
  const pending = by(/pending/i);
  const active = by(/active/i);
  const avgAdjustedSold = sold.length ? sold.reduce((s, c) => s + c.adjusted, 0) / sold.length : 0;
  return { sold, pending, active, avgAdjustedSold };
}

// ---- Shared with the MCP run_cma tool (also used by the app's CMA Builder) ----
// One block per property record in an SWFLMLS "Residential Customer Financial Report" text dump.
function splitRecords(fullText) {
  const parts = String(fullText || '').split(/Residential\s*Customer\s*Financial\s*Report/i);
  return parts.slice(1).map((p) => `Residential Customer Financial Report\n${p}`);
}
const defaultSubjectTiers = (rows) => Object.fromEntries(rows.map((r) => [r.key, r.tiers[0].id]));
const defaultValues = (rows) => {
  const v = {};
  rows.forEach((r) => { v[r.key] = {}; r.tiers.forEach((t) => { if (!t.base && t.value !== undefined) v[r.key][t.id] = String(t.value); }); });
  return v;
};
// Same rounded range the client PDF shows: nearest $5,000 center, $5,000 each way, ending in $900 / $400.
const rangeFor = (list) => { const c = Math.round(list / 5000) * 5000; return { low: c - 5100, high: c + 4900 }; };

module.exports = { STANDARD_FEATURES, EXTRA_PATTERNS, normalizeStatus, readRecord, tierFor, extrasFor, featureRows, tierValue, adjustmentLines, missingValues, runAnalysis, splitRecords, defaultSubjectTiers, defaultValues, rangeFor };
